export async function reverseGeocodePostal(lat, lng, options = {}) {
  if (typeof lat !== "number" || typeof lng !== "number") {
    throw new Error(
      "Latitude and longitude are required for reverse geocoding",
    );
  }

  const controller = new AbortController();
  const { signal } = options;
  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  // Use Google Maps Geocoding API
  const apiKey = process.env.REACT_APP_GEOCODE_API_KEY;

  if (!apiKey) {
    throw new Error(
      "Google Maps API key is missing. Please configure REACT_APP_GEOCODE_API_KEY.",
    );
  }

  const params = new URLSearchParams({
    latlng: `${lat},${lng}`,
    key: apiKey,
  });

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`,
    {
      method: "GET",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Reverse geocoding failed with status ${response.status}`);
  }

  const data = await response.json();

  if (data.status !== "OK" && data.status !== "ZERO_RESULTS") {
    throw new Error(
      `Geocoding API error: ${data.status}${
        data.error_message ? " - " + data.error_message : ""
      }`,
    );
  }

  if (!data.results || data.results.length === 0) {
    throw new Error("No address found for this location");
  }

  // Extract postal code and city from address components
  const result = data.results[0];
  let postalCode = null;
  let city = null;
  let province = null;

  for (const component of result.address_components) {
    if (component.types.includes("postal_code")) {
      postalCode = component.long_name.toUpperCase().replace(/\s+/g, " ");
    }
    if (component.types.includes("locality")) {
      city = component.long_name;
    }
    if (!city && component.types.includes("sublocality")) {
      city = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      province = component.short_name;
    }
  }

  // Fallback to formatted address parsing if city not found
  if (!city) {
    const addressParts = result.formatted_address.split(",");
    if (addressParts.length > 1) {
      city = addressParts[addressParts.length - 3]?.trim() || "Unknown";
    }
  }

  return {
    postalCode,
    city: city || "Unknown",
    province,
    lat,
    lng,
    raw: data,
  };
}

export async function lookupPostalDetails(postalCode, options = {}) {
  if (!postalCode) {
    throw new Error("Postal code is required");
  }

  const cleaned = postalCode.toString().trim();
  const controller = new AbortController();
  const { signal } = options;
  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  // Use Google Maps Geocoding API
  const apiKey =
    process.env.REACT_APP_GEOCODE_API_KEY ||
    "AIzaSyA55H6seNni8b3LFovJ0ibpGWp1ATQutJ8";

  if (!apiKey) {
    throw new Error(
      "Google Maps API key is missing. Please configure REACT_APP_GEOCODE_API_KEY.",
    );
  }

  const params = new URLSearchParams({
    address: cleaned,
    components: "country:CA", // Restrict to Canada
    key: apiKey,
  });

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`,
    {
      method: "GET",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Postal lookup failed with status ${response.status}`);
  }

  const data = await response.json();

  if (data.status !== "OK" && data.status !== "ZERO_RESULTS") {
    throw new Error(
      `Geocoding API error: ${data.status}${
        data.error_message ? " - " + data.error_message : ""
      }`,
    );
  }

  if (!data.results || data.results.length === 0) {
    throw new Error("Postal code not found");
  }

  const result = data.results[0];
  let resolvedPostal = null;
  let city = null;
  let province = null;
  let lat = result.geometry?.location?.lat;
  let lng = result.geometry?.location?.lng;

  for (const component of result.address_components) {
    if (component.types.includes("postal_code")) {
      resolvedPostal = component.long_name.toUpperCase().replace(/\s+/g, " ");
    }
    if (component.types.includes("locality")) {
      city = component.long_name;
    }
    if (!city && component.types.includes("sublocality")) {
      city = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      province = component.short_name;
    }
  }

  return {
    postalCode: resolvedPostal || cleaned.toUpperCase(),
    city: city || "Unknown",
    province,
    lat,
    lng,
    raw: data,
  };
}
